import type { BossDefinition } from './bosses'
import type { BossString } from './types'
import { HOUND_TEMPO, houndTempo } from './hound_tempo'

// 10-07 user: v2 is the release default. `?houndKit=v1` returns the old roster (comparison only);
//   the stored roster is never mutated.
let testOverride: boolean | undefined
export function setHoundKitV2ForTest(enabled: boolean | undefined): void { testOverride = enabled }
export function houndKitV2Enabled(): boolean {
  if (testOverride !== undefined) return testOverride
  if (typeof window === 'undefined') return true
  return new URLSearchParams(window.location.search).get('houndKit') !== 'v1'
}

// 10-06 user (kensyu): the beast is the "samurai general" tutorial-tier boss. Roar (floor band), bite-twice (not
//   readable vs a single bite) and delayed bite are cut from the kit; the attack defs stay for later bosses.
const words: BossString[] = [
  { id: 'hound-bite', beats: [{ attackId: 'houndBite' }], weight: 1 },
  { id: 'hound-lunge', beats: [{ attackId: 'houndLunge' }], weight: 1 },
  { id: 'hound-pounce', beats: [{ attackId: 'houndPounce' }], weight: 0.7 },
]
const actTwoWeights = [0.6, 0.8, 0.8]
const oldStrings: BossString[] = [
  ...words.map(s => ({ ...s, maxExecutes: 0 })),
  ...words.map((s, i) => ({ ...s, weight: actTwoWeights[i]!, unlockAtExecutes: 1 })),
  { id: 'hound-bite-pounce', beats: [{ attackId: 'houndBite' }, { attackId: 'houndPounce', gapMs: 450, chargeOverrideMs: 800 + 320 }], weight: 0.9, unlockAtExecutes: 1 },
]

// Set only by game.ts inside its DEV URL block. Release never reads houndMoves.
let oldMoves = false
export function setHoundMovesOld(enabled: boolean): void { oldMoves = enabled }
const tripleBeats: BossString['beats'] = [
  { attackId: 'houndTripleSwipe1' },
  { attackId: 'houndTripleSwipe2', gapMs: 300, chargeOverrideMs: 250 },
  { attackId: 'houndTripleSlam', gapMs: 300 },
]
// 三連の拍 (hound-triple と hound-triple-pounce が同じ拍の物を共有する)。描き直しの比較版 (hound_triple_redraw.ts) は
//   この物を直接書き換える (連が技から外れていても拍が見つかるように、連の一覧を通さず渡す)。
export function houndTripleBeats(): BossString['beats'] { return tripleBeats }
// 2026-10-08 ユーザー判断で三連 (hound-triple・hound-triple-pounce) を技から外した
//   (検収 kensyu-hound-triple.html の気づいたこと「この技いらないな」)。掴みと同じく、定義・絵・タイムライン・
//   描き直しの比較版のコードは残し、出る経路 (技の一覧・連・幕2の解禁) だけを止める。
//   DEV の ?houndTriple=on (game.ts) で今までの位置・重みに戻る。戻す時は描き直し版の絵で出す
//   (ユーザー 10-08 T-1〜T-3 がすべて「新しい方が良い」)。?houndTriple=old = 戻す＋描き直し前の絵。
//   ?houndTriple=redraw = 絵だけ描き直し版 (技には戻さない・?altAttacks=hound-triple と組んで見る用)。
//   ?altAttacks=hound-triple (または houndTripleSwipe1) の直接指定は altOnlyStrings から引くので常に効く。
let tripleOn = false
export function setHoundTriple(enabled: boolean): void { tripleOn = enabled }
export function houndTripleEnabled(): boolean { return tripleOn }
const TRIPLE_STRING_IDS = new Set(['hound-triple', 'hound-triple-pounce'])
const TRIPLE_ATTACK_IDS = ['houndTripleSwipe1', 'houndTripleSwipe2', 'houndTripleSlam']
const newWords: BossString[] = [
  ...words,
  { id: 'hound-swipe', beats: [{ attackId: 'houndSwipe' }], weight: 1 },
  { id: 'hound-triple', beats: tripleBeats, weight: 0.8 },
]
// 2026-10-08 ユーザー判断で掴み (houndGrab・赤) を技から外した (検収 H-1「掴みじゃなくて突進に見える・当たりも動きもおかしい・なくしても良い」→「なくす」)。
//   絵・攻撃定義・タイムラインは残し、出る経路 (技の一覧・連・幕2の解禁) だけを止める。赤い技はのしかかり1つになる。
//   DEV の ?houndGrab=on (game.ts) で今までの形 (幕2で重み 0.6 の連) に戻る。?altAttacks=houndGrab の直接指定は ATTACKS を直に引くので常に効く。
const grabString: BossString = { id: 'hound-grab', beats: [{ attackId: 'houndGrab' }], weight: 0.6, unlockAtExecutes: 1 }
let grabOn = false
export function setHoundGrab(enabled: boolean): void { grabOn = enabled }
export function houndGrabEnabled(): boolean { return grabOn }
// 掴みの連は幕2の解禁の並び (尾の薙ぎの次) へ差し込む。切っている間は strings に一切入らない。
function withGrab(list: BossString[]): BossString[] {
  if (!grabOn) return list
  const at = list.findIndex(s => s.id === 'hound-tail') + 1
  return [...list.slice(0, at), grabString, ...list.slice(at)]
}
const strings: BossString[] = [
  ...newWords.map(s => ({ ...s, maxExecutes: 0 })),
  ...newWords.map((s, i) => ({ ...s, weight: [0.6, 0.8, 0.8, 0.8, 0.9][i]!, unlockAtExecutes: 1 })),
  { id: 'hound-tail', beats: [{ attackId: 'houndTailSweep' }], weight: 0.5, unlockAtExecutes: 1 },
  // 掴みの連 (hound-grab) はここへ入れない。resolveHoundKit が ?houndGrab=on の時だけ足す。
  oldStrings[oldStrings.length - 1]!,
  { id: 'hound-swipe-lunge', beats: [{ attackId: 'houndSwipe' }, { attackId: 'houndLunge', gapMs: 300 }], weight: 0.7, unlockAtExecutes: 1 },
  { id: 'hound-triple-pounce', beats: [...tripleBeats, { attackId: 'houndPounce', gapMs: 450, chargeOverrideMs: 1120 }], weight: 0.6, unlockAtExecutes: 1 },
]

// 三連を切っている間は連の一覧から三連を含む連を抜く (残りの並び・重みはそのまま)。
function withTriple(list: BossString[]): BossString[] {
  return tripleOn ? list : list.filter(s => !TRIPLE_STRING_IDS.has(s.id))
}
// ?altAttacks の直接指定用。幕1の形 (解禁・上限なし) で1本ずつ。
const tripleAltStrings: BossString[] = [
  { id: 'hound-triple', beats: tripleBeats, weight: 0.8 },
  strings.find(s => s.id === 'hound-triple-pounce')!,
].map(({ unlockAtExecutes: _u, maxExecutes: _m, ...s }) => s)

// 幕2の三連 (新・2026-10-08 ユーザー「3連攻撃は一回取り下げたけど2幕に入れてほしい／ただし、読みにくい技だったので
//   他のモーションなどで作った方が良い。作りやすい技でいいよ。タイミングは同じが良いと思う」)。
//   爪→牙→叩きつけ (attacks.ts houndTriple2Claw/Fang/Slam・絵は使い回し)。拍は描き直し版の三連と同じ
//   (拍間 50ms・2拍目の溜め 650・3拍目の溜め 1100＝当たりから当たり 830ms・1280ms)。幕2の解禁だけ (幕1には出ない)・重みは旧三連の幕2と同じ 0.9。
//   🔵 2026-10-08 ユーザー採用 (kensyu-hound-tune2.html：読みやすさ「読みやすい」・版 c とも「はい」) = 配布版の既定オン。
//   比較は DEV の ?houndTriple2=off (game.ts の DEV の URL ブロック) で切る。
//   切っている間も ?altAttacks=hound-triple2 (altOnlyStrings) で単独に出せる。
const TRIPLE2_ATTACK_IDS = ['houndTriple2Claw', 'houndTriple2Fang', 'houndTriple2Slam']
const triple2Beats: BossString['beats'] = [
  { attackId: 'houndTriple2Claw' },
  { attackId: 'houndTriple2Fang', gapMs: 50 },
  { attackId: 'houndTriple2Slam', gapMs: 50 },
]
const triple2String: BossString = { id: 'hound-triple2', beats: triple2Beats, weight: 0.9, unlockAtExecutes: 1 }
const triple2AltString: BossString = { id: 'hound-triple2', beats: triple2Beats, weight: 0.9 }
let triple2On = true
export function setHoundTriple2(enabled: boolean): void { triple2On = enabled }
export function houndTriple2Enabled(): boolean { return triple2On }
function withTriple2(list: BossString[]): BossString[] {
  return triple2On ? [...list, triple2String] : list
}

// 尾の薙ぎ (hound-tail) を普段の抽選から外す (2026-10-08 ユーザー：問い「尾の薙ぎをどうする？」→「背後に回った時だけ出す」)。
//   ユーザーの気づき「尻尾攻撃？かなにかをしている技、後ろを向く技なんだけど、攻撃に被弾したことないしどういう技なのかわからない」。
//   抽選から出ると主人公が前にいる時に背中側 (誰もいない側) を薙いでいた (シム・docs/research/2026-10-08-hound-hp-posture-tune.md §11)。
//   背中側に回られた時の咎め (backAttackByExecutes・幕2から・facePlayerWithRear) は残す。
//   DEV の ?houndTailPool=on (game.ts) で抽選に戻す。攻撃定義・絵は残す。掴みの差し込み位置 (withGrab) は尾の位置を基準にするので、
//   外すのは掴みを差し込んだ後。
let tailPoolOn = false
export function setHoundTailPool(enabled: boolean): void { tailPoolOn = enabled }
export function houndTailPoolEnabled(): boolean { return tailPoolOn }
function withTailPool(list: BossString[]): BossString[] {
  return tailPoolOn ? list : list.filter(s => s.id !== 'hound-tail')
}

export function resolveHoundKit(def: BossDefinition): BossDefinition {
  if (def.id !== 'dream-hound' || !houndKitV2Enabled()) return def
  // 単独で見る時は DEV の ?soloAttack=houndPounce (game.ts の debugSoloAttack が連を外す)。
  //   配布版で読む URL は houndKit だけにする (soloAttack は DEV 専用のまま)。
  // 幕ごとのテンポ (hound_tempo.ts): 次の攻撃までの待ちだけを、幕1 は idleGapMul・幕2 (最後の幕) は finalRoomTempoMul で伸び縮みさせる。
  const t = HOUND_TEMPO[houndTempo()]
  const tempo = t.act1GapMul === 1 && t.act2GapMul === 1 ? {}
    : { idleGapMul: (def.idleGapMul ?? 1) * t.act1GapMul, finalRoomTempoMul: t.act2GapMul / t.act1GapMul }
  return {
    ...def,
    ...tempo,
    baseAttackIds: [...def.baseAttackIds, 'houndPounce', ...(oldMoves ? [] : ['houndSwipe', ...(tripleOn ? TRIPLE_ATTACK_IDS : []), ...(triple2On ? TRIPLE2_ATTACK_IDS : []), 'houndTailSweep', ...(grabOn ? ['houndGrab'] : [])])],
    strings: oldMoves ? oldStrings : withTriple2(withTriple(withTailPool(withGrab(strings)))),
    ...(oldMoves || (tripleOn && triple2On) ? {} : { altOnlyStrings: [...(tripleOn ? [] : tripleAltStrings), ...(triple2On ? [] : [triple2AltString])] }),
    ...(oldMoves ? {} : { backAttackByExecutes: [{ atExecutes: 1, attackId: 'houndTailSweep', facePlayerWithRear: true }] }),
  }
}
